export const PLATFORMS = ["facebook", "instagram", "tiktok"] as const;
export type Platform = (typeof PLATFORMS)[number];

export interface PlatformLimits {
  captionMax: number;
  image: {
    maxBytes: number;
    mimes: string[];
    maxCount: number;
  };
  video: {
    maxBytes: number;
    mimes: string[];
    minDurationMs: number;
    maxDurationMs: number;
  } | null;
  /** can images and a video be mixed in one post */
  allowsMixedMedia: boolean;
}

/**
 * Conservative published limits. VERIFY against current platform docs before
 * relying on exact numbers — platforms change these without notice.
 * Sources to re-check:
 *  - Facebook Graph API (Pages posting)
 *  - Instagram Graph API content publishing
 *  - TikTok Content Posting API
 */
export const PLATFORM_LIMITS: Record<Platform, PlatformLimits> = {
  facebook: {
    captionMax: 63206,
    image: { maxBytes: 10 * 1024 * 1024, mimes: ["image/jpeg", "image/png", "image/gif"], maxCount: 10 },
    video: {
      maxBytes: 1024 * 1024 * 1024,
      mimes: ["video/mp4", "video/quicktime"],
      minDurationMs: 1000,
      maxDurationMs: 4 * 60 * 60 * 1000,
    },
    allowsMixedMedia: false,
  },
  instagram: {
    captionMax: 2200,
    image: { maxBytes: 8 * 1024 * 1024, mimes: ["image/jpeg", "image/png"], maxCount: 10 },
    video: {
      maxBytes: 1024 * 1024 * 1024,
      mimes: ["video/mp4", "video/quicktime"],
      minDurationMs: 3000,
      maxDurationMs: 15 * 60 * 1000,
    },
    allowsMixedMedia: true, // carousels can mix
  },
  tiktok: {
    captionMax: 2200,
    image: { maxBytes: 20 * 1024 * 1024, mimes: ["image/jpeg", "image/webp"], maxCount: 35 },
    video: {
      maxBytes: 4 * 1024 * 1024 * 1024,
      mimes: ["video/mp4", "video/quicktime", "video/webm"],
      minDurationMs: 3000,
      maxDurationMs: 10 * 60 * 1000,
    },
    allowsMixedMedia: false,
  },
};

/** IG-only: API-published posts allowed per rolling 24h per account. Re-verify. */
export const INSTAGRAM_DAILY_PUBLISH_LIMIT = 50;
